<template>
  <div class="space-y-8">
    <SectionHeader :title="__('Projects')">
      <template #icon>
        <Icon class="text-3xl text-primary" name="tactic" is-filled />
      </template>
      <template v-if="canEdit && !hasNoProjects" #button>
        <div class="ml-auto">
          <Button
            class="flex items-center gap-1.5"
            variant="ghost"
            @click="handleAddButtonClick"
          >
            <Icon class="text-lg" name="add_box" is-filled />
            <span>{{ __('Add project') }}</span>
          </Button>
        </div>
      </template>
    </SectionHeader>

    <div class="space-y-4">
      <div v-if="!projects.length" class="shadow-card relative overflow-hidden rounded-lg bg-white p-6 md:p-8">
        <div v-if="hasNoProjects" class="absolute right-0 top-0 rounded-l-full bg-secondary px-3 py-1">
        <span class="block text-xs leading-none text-white">
          {{ progress }}
        </span>
        </div>
        <InputCheckbox
            id="hasNoProjects"
            :value="hasNoProjects"
            :label="__('I have not had any previous projects')"
            @update:value="handleHasNoProjectsUpdate"
        />
      </div>

    <div v-if="!hasNoProjects" class="shadow-card relative overflow-hidden rounded-lg bg-white p-6 md:p-8">

      <div v-if="progress" class="absolute right-0 top-0 rounded-l-full bg-secondary px-3 py-1">
        <span class="block text-xs leading-none text-white">
          {{ progress }}
        </span>
      </div>

      <EmptySection v-if="!projects.length" :title="__('Add your projects')">
        <template #icon>
          <Icon class="text-xl text-zinc-200" name="tactic" is-filled />
        </template>
      </EmptySection>

      <div v-else class="space-y-8">
          <Project
            v-for="project in isListExpanded ? projects : projects.slice(0, 3)"
            :key="project.id"
            :project="project"
            :can-edit="canEdit"
            @edit="handleEditButtonClick(project)"
            @delete="handleDeleteButtonClick(project)"
          />

          <Button
            v-if="projects.length > 3"
            class="mx-auto"
            variant="outline"
            @click="handleExpandListButtonClick"
          >
            {{ isListExpanded ? __('See less') : __('See more') }}
          </Button>
        </div>

        <ProjectFormModal
          v-if="isFormModalOpen"
          :project="selected"
          @cancel="handleCancelButtonClick"
          @save="handleSaveButtonClick"
        />

        <ProjectDeleteModal
          v-if="isDeleteModalOpen"
          :project="selected"
          @cancel="handleCancelButtonClick"
          @continue="handleContinueButtonClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from 'vue';
  import { useToast } from 'vue-toastification';
  import { Button } from '@/components/block/button';
  import { Icon } from '@/components/block/icon';
  import { EmptySection } from '@/pages/components/empty-section';
  import { SectionHeader } from '@/pages/components/section-header';
  import { Project } from './components/project';
  import { ProjectDeleteModal } from './components/project-delete-modal';
  import { ProjectFormModal } from './components/project-form-modal';
  import { InputCheckbox } from "../../../../../components/form/input-checkbox";

  const props = defineProps({
    data: {
      type: Array,
      required: true
    },
    fields: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      required: true
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    hasNoProjects: {
      type: Boolean,
      required: true
    },
  });

  const emits = defineEmits(['update-is-editing', 'update-percentages', 'update-data']);
  const toast = useToast();

  const progress = computed(() => {
    return fields.value.total
      ? `${fields.value.completed}/${fields.value.total}`
      : '';
  });

  const fields = ref(JSON.parse(JSON.stringify(props.fields)));
  const projects = ref(JSON.parse(JSON.stringify(props.data)));
  const selected = ref(null);
  const isFormModalOpen = ref(false);
  const isDeleteModalOpen = ref(false);
  const pendingAction = ref('');
  const isListExpanded = ref(false);
  const hasNoProjects = ref(props.hasNoProjects);

  async function handleHasNoProjectsUpdate(value) {

    try {
      const response = await axios.put(
          route('redesign.cv.update.no_projects'),
          { no_projects: value }
      );

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      fields.value = response.data.fields;
      hasNoProjects.value = value;

      emits('update-percentages', {
        sectionPercentage: response.data.section_percentage,
        resumePercentage: response.data.percentage
      });
    } catch (error) {
      toast.error(error.response?.data?.message ||  __('An unexpected error has occured. Please try again later.'));
    }
  }
  function handleExpandListButtonClick() {
    isListExpanded.value = !isListExpanded.value;
  }

  function handleAddButtonClick() {
    selected.value = null;
    pendingAction.value = 'add';

    emits('update-is-editing', true);
  }

  function handleEditButtonClick(project) {
    selected.value = JSON.parse(JSON.stringify(project));
    pendingAction.value = 'edit';

    emits('update-is-editing', true);
  }

  function handleDeleteButtonClick(project) {
    selected.value = JSON.parse(JSON.stringify(project));
    pendingAction.value = 'delete';

    emits('update-is-editing', true);
  }

  function handleCancelButtonClick() {
    emits('update-is-editing', false);
  }

  function handleSaveButtonClick(data) {
    const existingProject = projects.value.find(
      (project) => project.id === data.project.id
    );

    if (existingProject) {
      projects.value = projects.value.map((project) => {
        if (project.id === data.project.id) {
          return data.project;
        }

        return project;
      });
    } else {
      projects.value = [data.project, ...projects.value];
    }

    projects.value = sortProjects(projects.value)

    fields.value = data.fields;

    emits('update-data', projects.value);
    emits('update-is-editing', false);
    emits('update-percentages', {
      sectionPercentage: data.sectionPercentage,
      resumePercentage: data.resumePercentage
    });
  }

  function sortProjects(list) {
    return list.sort((a, b) => {
      if (a.current && !b.current) return -1;
      if (!a.current && b.current) return 1;

      const aEnd = a.end_date ? new Date(a.end_date).getTime() : 0;
      const bEnd = b.end_date ? new Date(b.end_date).getTime() : 0;
      if (bEnd !== aEnd) return bEnd - aEnd;

      const aStart = a.start_date ? new Date(a.start_date).getTime() : 0;
      const bStart = b.start_date ? new Date(b.start_date).getTime() : 0;
      return bStart - aStart;
    });
  }

  function handleContinueButtonClick(data) {
    projects.value = projects.value.filter(
      (project) => project.id !== data.project.id
    );

    fields.value = data.fields;

    projects.value = projects.value.filter(e => e.id !== selected.value.id);
    emits('update-data', projects.value);
    emits('update-is-editing', false);
    emits('update-percentages', {
      sectionPercentage: data.sectionPercentage,
      resumePercentage: data.resumePercentage
    });
  }

  watch(
    () => props.isEditing,
    (value) => {
      if (pendingAction.value === 'add' || pendingAction.value === 'edit') {
        isFormModalOpen.value = value;
      } else if (pendingAction.value === 'delete') {
        isDeleteModalOpen.value = value;
      }
    }
  );
</script>
